// SessionsListProvider — sessions-list suite root.
//
// Owns:
//  - Filter state for the sidebar list (search query, archived toggle, includeSession id).
//  - The underlying infinite-query that fetches paginated sessions.
//  - The flattened pages, totalCount, status, and pagination handles consumed by the
//    suite's hooks.
//
// Mounts at app root immediately under MessageModalProvider. Components consume the
// suite via useSessionsList / useSessionsFilters / use-create-session etc.; they do
// not import this Context directly.

"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ROUTE_CONTRACTS } from "@cabal/shared/api-client";
import type { Session } from "@cabal/shared/domain/session/types.js";
import { useApi } from "@/contexts/api";
import { useRouteAuthEnabled } from "@/hooks/routing";
import {
  QUERY_STATUS,
  type QueryResult,
  type QueryStatus,
} from "@/types/async-protocol";
import { sessionsListKeys } from "./keys";

const SESSIONS_LIST_PAGE_SIZE = 20;

export interface SessionsFilters {
  search: string;
  archived: boolean;
  includeSessionId: string | null;
}

export interface SessionsListSnapshot extends QueryResult<Session[], Error> {
  totalCount: number | null;
  fetchMore: () => void;
  hasMore: boolean;
  isFetchingMore: boolean;
}

interface SessionsListContextValue {
  filters: SessionsFilters;
  setFilter: <K extends keyof SessionsFilters>(
    key: K,
    value: SessionsFilters[K],
  ) => void;
  list: SessionsListSnapshot;
}

const INITIAL_FILTERS: SessionsFilters = {
  search: "",
  archived: false,
  includeSessionId: null,
};

const SessionsListContext = createContext<SessionsListContextValue | null>(
  null,
);

interface SessionsPage {
  items: Session[];
  nextCursor: string | null;
  totalCount: number;
}

type ApiClient = ReturnType<typeof useApi>;

interface SessionsListRequestParams {
  cursor: string | undefined;
  includeArchived: true | undefined;
  search: string | undefined;
}

interface FetchSessionsListPageInput {
  api: ApiClient;
  filters: SessionsFilters;
  includeSessionId: string | null;
  pageParam: unknown;
}

function matchesSessionFilters(
  session: Session,
  filters: SessionsFilters,
): boolean {
  const archivedMatches = filters.archived
    ? session.archivedAt != null
    : session.archivedAt == null;
  if (!archivedMatches) return false;

  const search = filters.search.trim().toLowerCase();
  if (!search) return true;
  return session.name.toLowerCase().includes(search);
}

function getSessionsListRequestParams(
  input: FetchSessionsListPageInput,
): SessionsListRequestParams {
  const cursor = typeof input.pageParam === "string" ? input.pageParam : undefined;

  return {
    cursor,
    includeArchived: input.filters.archived ? true : undefined,
    search: input.filters.search.trim() || undefined,
  };
}

async function fetchPinnedSession(input: {
  api: ApiClient;
  includeSessionId: string | null;
  shouldFetch: boolean;
}): Promise<Session | null> {
  if (!input.shouldFetch || !input.includeSessionId) {
    return null;
  }

  return input.api.sessions
    .byId(input.includeSessionId)
    .get()
    .catch(() => null);
}

function buildSessionsPageItems(input: {
  items: Session[];
  filters: SessionsFilters;
  includeSessionId: string | null;
  isFirstPage: boolean;
  pinnedSession: Session | null;
}): Session[] {
  const items = input.includeSessionId
    ? input.items.filter((item) => item.id !== input.includeSessionId)
    : input.items;

  if (!input.isFirstPage || !input.pinnedSession) {
    return items;
  }

  if (!matchesSessionFilters(input.pinnedSession, input.filters)) {
    return items;
  }

  return [input.pinnedSession, ...items];
}

async function fetchSessionsListPage(
  input: FetchSessionsListPageInput,
): Promise<SessionsPage> {
  const requestParams = getSessionsListRequestParams(input);
  const isFirstPage = !requestParams.cursor;

  const [listResponse, countResponse, pinnedSession] = await Promise.all([
    input.api.sessions.list({
      query: {
        includeArchived: requestParams.includeArchived,
        includeSession: input.includeSessionId ?? undefined,
        name: requestParams.search,
        limit: SESSIONS_LIST_PAGE_SIZE,
        cursor: requestParams.cursor,
      },
    }),
    input.api.sessions.count({
      query: {
        includeArchived: requestParams.includeArchived,
        includeSession: input.includeSessionId ?? undefined,
        name: requestParams.search,
      },
    }),
    fetchPinnedSession({
      api: input.api,
      includeSessionId: input.includeSessionId,
      shouldFetch: isFirstPage,
    }),
  ]);

  return {
    items: buildSessionsPageItems({
      items: listResponse.items,
      filters: input.filters,
      includeSessionId: input.includeSessionId,
      isFirstPage,
      pinnedSession,
    }),
    nextCursor: listResponse.nextCursor,
    totalCount: countResponse.count,
  };
}

function mapInfiniteStatus(input: {
  isError: boolean;
  isSuccess: boolean;
}): QueryStatus {
  if (input.isError) return QUERY_STATUS.Errored;
  if (input.isSuccess) return QUERY_STATUS.Success;
  return QUERY_STATUS.Loading;
}

export function SessionsListProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const routeEnabled = useRouteAuthEnabled({
    route: ROUTE_CONTRACTS.sessions.list,
  });

  const [filters, setFilters] = useState<SessionsFilters>(INITIAL_FILTERS);

  const setFilter = useCallback(
    <K extends keyof SessionsFilters>(key: K, value: SessionsFilters[K]) => {
      setFilters((prev) =>
        prev[key] === value ? prev : { ...prev, [key]: value },
      );
    },
    [],
  );

  const includeSessionId = filters.includeSessionId?.trim() || null;

  const result = useInfiniteQuery<SessionsPage, Error>({
    queryKey: sessionsListKeys.list({
      search: filters.search,
      archived: filters.archived,
      includeSessionId: includeSessionId ?? undefined,
    }),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      fetchSessionsListPage({
        api,
        filters,
        includeSessionId,
        pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: routeEnabled,
    staleTime: 30_000,
  });

  const resultData = result.data;
  const sessions = useMemo<Session[]>(() => {
    if (!resultData) return [];
    return resultData.pages.flatMap((page) => page.items);
  }, [resultData]);

  const resultDataValue = result.data;
  const totalCount = useMemo<number | null>(() => {
    if (!resultDataValue || resultDataValue.pages.length === 0) return null;
    return resultDataValue.pages[0].totalCount;
  }, [resultDataValue]);

  const refetch = useCallback(() => {
    void result.refetch();
  }, [result]);

  const fetchMore = useCallback(() => {
    if (result.hasNextPage && !result.isFetchingNextPage) {
      void result.fetchNextPage();
    }
  }, [result]);

  const resultIsError = result.isError;
  const resultIsSuccess = result.isSuccess;
  const resultError = result.error;
  const resultHasNextPage = result.hasNextPage;
  const resultIsFetchingNextPage = result.isFetchingNextPage;
  const list = useMemo<SessionsListSnapshot>(
    () => ({
      data: sessions,
      totalCount,
      status: mapInfiniteStatus({
        isError: resultIsError,
        isSuccess: resultIsSuccess,
      }),
      error: resultError ?? null,
      refetch,
      fetchMore,
      hasMore: resultHasNextPage ?? false,
      isFetchingMore: resultIsFetchingNextPage,
    }),
    [
      sessions,
      totalCount,
      resultIsError,
      resultIsSuccess,
      resultError,
      resultHasNextPage,
      resultIsFetchingNextPage,
      refetch,
      fetchMore,
    ],
  );

  const value = useMemo<SessionsListContextValue>(
    () => ({ filters, setFilter, list }),
    [filters, setFilter, list],
  );

  return (
    <SessionsListContext.Provider value={value}>
      {children}
    </SessionsListContext.Provider>
  );
}

export function useSessionsListContext(): SessionsListContextValue {
  const ctx = useContext(SessionsListContext);
  if (!ctx) throw new Error("SessionsListProvider not mounted");
  return ctx;
}
